import { useCallback, useEffect, useMemo, useState } from 'react';

import { useGetUser } from 'app/api/hooks/core/v1/auth/user';
import { keys } from 'app/config/keys';
import { UiTheme } from 'app/state/GeneralContext/contexts';
import { useGeneralDispatch, useGeneralState } from 'app/state/GeneralContext/hooks';

const uiThemeLocalStorageKey = 'uiTheme';

const prefersColorSchemeKey = '(prefers-color-scheme: dark)';
const initialPrefersDarkMode = window.matchMedia && window.matchMedia(prefersColorSchemeKey).matches;

export const useCanUseDarkMode = () => {
  const user = useGetUser();

  // only allow dark mode at 100% in non-production environments for now
  const canUseDarkMode = keys.NODE_ENV !== 'production';

  // only offering dark mode for now in production after a user is signed in
  // there is a bit more cleanup to do for dark mode on Okta sign-in pages
  // after the entire Okta flow looks good in dark mode, we can remove this function entirely
  return canUseDarkMode || !!user;
};

export const getLocalStorageTheme = () => {
  const localStorageTheme = localStorage.getItem(uiThemeLocalStorageKey) as UiTheme;

  return localStorageTheme ?? 'system';
};

export const getPrefersDarkMode = () => {
  return window.matchMedia(prefersColorSchemeKey).matches;
};

export const useUiTheme = () => {
  const [prefersDarkMode, setPrefersDarkMode] = useState(initialPrefersDarkMode);

  const generalState = useGeneralState();
  const generalDispatch = useGeneralDispatch();

  const contextTheme = generalState.uiTheme;

  const localStorageTheme = getLocalStorageTheme();

  const updateTheme = useCallback((newTheme: UiTheme) => {
    localStorage.setItem(uiThemeLocalStorageKey, newTheme);
    generalDispatch({
      payload: { uiTheme: newTheme },
      type: 'updateUiTheme',
    });
  }, [generalDispatch]);

  useEffect(() => {
    if (localStorageTheme !== contextTheme) {
      generalDispatch({
        payload: { uiTheme: localStorageTheme },
        type: 'updateUiTheme',
      });
    }
  }, [
    contextTheme,
    generalDispatch,
    localStorageTheme,
    updateTheme,
  ]);

  const eventListener = useCallback((event: MediaQueryListEvent) => {
    setPrefersDarkMode(event.matches);
  }, []);

  useEffect(() => {
    window.matchMedia(prefersColorSchemeKey).addEventListener('change', eventListener);

    return () => {
      window.matchMedia(prefersColorSchemeKey).removeEventListener('change', eventListener);
    };
  }, [eventListener]);

  return useMemo(() => {
    return {
      prefersDarkMode,
      theme: contextTheme,
      updateTheme,
    };
  }, [
    contextTheme,
    prefersDarkMode,
    updateTheme,
  ]);
};
